import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

// Feature-scoped API module for car master data (makes / models / colours), the
// moderation queue for customer-suggested makes and models, and the read-only views
// over customer vehicles. Uses the shared axios instance (Bearer auth + 401 handling).
//
// Convention, matching pickupBlockApi.js: reads return data or a safe default and
// toast on error; mutations return the record or null and let the caller toast success.
//
// Makes/models/colours are GLOBAL, not merchant-scoped — a Toyota is a Toyota for
// every brand — so none of the lookup calls take a merchantId. Customer vehicles are
// merchant-scoped and those calls do.

const read = async (url, params, fallback, label) => {
  try {
    const res = await axiosInstance.get(url, { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? `Error loading ${label}`);
    return fallback;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? `Error loading ${label}`);
    return fallback;
  }
};

const mutate = async (method, url, payload, label) => {
  try {
    const res = await axiosInstance[method](url, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? `Error ${label}`);
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? `Error ${label}`);
    return null;
  }
};

// ---- Makes ----------------------------------------------------------------
// status defaults to approved so the lookup tab never shows pending suggestions —
// those live in their own queue.
export const getCarMakes = (params = { status: "approved" }) =>
  read("/api/admin/car-makes", params, [], "car makes");

export const createCarMake = (payload) =>
  mutate("post", "/api/admin/car-makes", payload, "creating make");

export const updateCarMake = (id, payload) =>
  mutate("put", `/api/admin/car-makes/${id}`, payload, "updating make");

export const deleteCarMake = (id) =>
  mutate("delete", `/api/admin/car-makes/${id}`, undefined, "deleting make");

// ---- Models ---------------------------------------------------------------
export const getCarModels = (params = { status: "approved" }) =>
  read("/api/admin/car-models", params, [], "car models");

export const createCarModel = (payload) =>
  mutate("post", "/api/admin/car-models", payload, "creating model");

export const updateCarModel = (id, payload) =>
  mutate("put", `/api/admin/car-models/${id}`, payload, "updating model");

export const deleteCarModel = (id) =>
  mutate("delete", `/api/admin/car-models/${id}`, undefined, "deleting model");

// ---- Colours --------------------------------------------------------------
export const getCarColors = () => read("/api/admin/car-colors", {}, [], "car colours");

export const createCarColor = (payload) =>
  mutate("post", "/api/admin/car-colors", payload, "creating colour");

export const updateCarColor = (id, payload) =>
  mutate("put", `/api/admin/car-colors/${id}`, payload, "updating colour");

export const deleteCarColor = (id) =>
  mutate("delete", `/api/admin/car-colors/${id}`, undefined, "deleting colour");

// ---- Moderation queue -----------------------------------------------------
// -> { items: [{ id, type, name, makeName, customerCount, submittedBy, createdAt }], count }
// Already sorted by customerCount desc server-side: the count is the ranking signal,
// so an admin never has to judge whether an unfamiliar brand is real.
export const getCarSuggestions = () =>
  read("/api/admin/car-suggestions", {}, { items: [], count: 0 }, "suggestions");

// Approve takes the corrected name + Arabic translation in the same call — promoting
// a raw customer-typed string would ship an untranslated name to the app.
export const approveCarSuggestion = (id, payload) =>
  mutate("post", `/api/admin/car-suggestions/${id}/approve`, payload, "approving suggestion");

// payload: { type, targetId }. Repoints every referencing vehicle onto the canonical
// row and deletes the pending one.
export const mergeCarSuggestion = (id, payload) =>
  mutate("post", `/api/admin/car-suggestions/${id}/merge`, payload, "merging suggestion");

// payload: { type }. Stays visible to customers already using it, never goes global.
export const rejectCarSuggestion = (id, payload) =>
  mutate("post", `/api/admin/car-suggestions/${id}/reject`, payload, "rejecting suggestion");

// ---- Customer vehicles (read-only) ----------------------------------------
// -> { query, results: [{ vehicle, customer, recentOrders }] }
export const searchVehiclesByPlate = (plate, merchantId) =>
  read(
    "/api/admin/vehicle-search",
    { plate, merchantId },
    { query: "", results: [] },
    "plate search"
  );

export const getCustomerVehicles = (userId, merchantId) =>
  read("/api/admin/customer-vehicles", { userId, merchantId }, [], "customer vehicles");
